import React, { useState, useEffect } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronDown } from 'lucide-react'

interface SecondaryNavCNProps {
	links: Partial<{
		linkText: string
		linkTo: string
		icon: React.ComponentType<{ className?: string }>
		end: boolean
	}>[]
	backLink?: { link: string; title: string }
	sourceId?: string
	sourceName?: string
}

const SecondaryNavCN = ({ links, backLink, sourceId, sourceName }: SecondaryNavCNProps) => {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const currentRoute = useLocation()

	useEffect(() => {
		setMobileMenuOpen(false)
	}, [currentRoute.pathname])

	const rootSource = (link: string) => {
		const parts = link.split('/')
		return parts.length > 1 ? parts[1] : null
	}

	const activeLink = links.find(
		link => link.linkTo && currentRoute.pathname.endsWith(link.linkTo.split('/').pop() || ''),
	)
	const activeLinkText = activeLink?.linkText || links[0]?.linkText || 'Menu'

	const backTo =
		backLink?.link && rootSource(backLink.link) === 'portfolios'
			? backLink.link
			: `${backLink?.link}/${sourceId || ''}`

	return (
		<div className="flex-shrink-0 border-b border-gray-200 bg-white">
			<div className="flex items-center h-10 px-6">
				{/* Back link */}
				{backLink?.link && backLink?.title && (
					<Link
						to={backTo}
						className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#0080fe] no-underline transition-colors mr-4 flex-shrink-0"
					>
						<ChevronLeft className="w-4 h-4" />
						<span className="hidden md:inline">{rootSource(backLink.link) === 'portfolios' ? backLink.title : sourceName}</span>
					</Link>
				)}

				{/* Desktop: horizontal tabs */}
				<nav className="hidden md:flex items-center gap-1 overflow-x-auto">
					{links.map((link, index) => {
						if (!link.linkTo || !link.linkText) return null
						const Icon = link.icon
						return (
							<NavLink
								key={index}
								to={link.linkTo}
								end={link.end}
								className={({ isActive }) =>
									`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full no-underline transition-colors whitespace-nowrap ${
										isActive
											? 'bg-[#0080fe]/10 text-[#0080fe] font-medium'
											: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
									}`
								}
							>
								{Icon && <Icon className="w-3.5 h-3.5" />}
								{link.linkText}
							</NavLink>
						)
					})}
				</nav>

				{/* Mobile: dropdown toggle */}
				<div className="md:hidden flex-1">
					<button
						type="button"
						onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
						className="flex items-center gap-2 text-sm font-medium text-gray-700"
						aria-expanded={mobileMenuOpen}
					>
						{(() => {
							const ActiveIcon = activeLink?.icon
							return ActiveIcon ? <ActiveIcon className="w-4 h-4 text-gray-500" /> : null
						})()}
						<span>{activeLinkText}</span>
						<ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
					</button>
				</div>
			</div>

			{/* Mobile dropdown */}
			{mobileMenuOpen && (
				<div className="md:hidden border-t border-gray-100 bg-white">
					{links.map((link, index) => {
						if (!link.linkTo || !link.linkText) return null
						const Icon = link.icon
						return (
							<NavLink
								key={index}
								to={link.linkTo}
								end={link.end}
								onClick={() => setMobileMenuOpen(false)}
								className={({ isActive }) =>
									`flex items-center gap-2 px-6 py-2.5 text-sm no-underline transition-colors ${
										isActive ? 'text-[#0080fe] bg-highlight font-medium' : 'text-gray-600 hover:bg-gray-50'
									}`
								}
							>
								{Icon && <Icon className="w-4 h-4" />}
								{link.linkText}
							</NavLink>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default SecondaryNavCN
